<script lang="ts">
import type { CreateElement, RenderContext } from "vue";
import Vue from "vue";
import type { Renderable } from "@/components/controls/grid/BmGridData";
import { truncate } from "lodash";
import type { TextTruncationOptions } from "@/components/controls/grid/builder/ColumnBuilders";
import {
  prefix,
  suffix,
} from "@/components/controls/grid/renderers/rendererUtilities";

let nullPlaceholder = "-";

function getLocation(
  context: Renderable,
  item: Object,
  value: any
): Location | undefined {
  const renderingOpts = context.rendererMetadata;
  if (renderingOpts && renderingOpts.locationExtractor) {
    if (typeof renderingOpts.locationExtractor === "function") {
      return renderingOpts.locationExtractor(item, value);
    }
  }
  return undefined;
}

function getExternalLink(
  context: Renderable,
  item: Object,
  value: any
): string | undefined {
  let link: string | undefined;
  const renderingOpts = context.rendererMetadata;

  if (renderingOpts && renderingOpts.externalLinkExtractor) {
    if (typeof renderingOpts.externalLinkExtractor === "function") {
      link = renderingOpts.externalLinkExtractor(item, value);
    }
  }

  if (!link) {
    return undefined;
  }

  // append http:// to the extracted url if there isn't http(s)
  // already to prevent Vue from rendering the href as
  // `https://localhost:7000/www.google.com`
  if (!/^https?:\/\//i.test(link)) {
    link = "http://" + link;
  }

  return link;
}

function iconSuffixExtractor(
  context: Renderable,
  item: Object,
  value: any
): string | undefined {
  const renderingOpts = context.rendererMetadata;
  if (renderingOpts && renderingOpts.iconSuffixExtractor) {
    if (typeof renderingOpts.iconSuffixExtractor === "function") {
      return renderingOpts.iconSuffixExtractor(item, value);
    } else if (typeof renderingOpts.iconSuffixExtractor === "string") {
      return renderingOpts.iconSuffixExtractor;
    }
  }
  return undefined;
}

function cellContent(rawValue: any, context: Renderable, item: Object) {
  const options = context.rendererMetadata.textTruncationOptions as
    | TextTruncationOptions<any>
    | undefined;
  const truncateValue = options && !context.rendererMetadata.isAgTruncated;

  const value = truncateValue
    ? truncate(rawValue, {
        length: options.length,
        omission: options.omission,
        separator: options.separator,
      })
    : rawValue;

  if (value && value !== nullPlaceholder) {
    const p = prefix(context, item, value);
    const s = suffix(context, item, value);
    return `${p ? p + " " : ""}${value}${s ? " " + s : ""}`;
  }
  return nullPlaceholder;
}

export default Vue.extend({
  name: "DataCellText",
  functional: true,
  props: {
    item: { default: () => undefined, required: false },
    value: { required: true },
    context: {
      type: Object as () => Renderable,
      required: false,
    },
  },
  render: function (createElement: CreateElement, context: RenderContext) {
    const location = getLocation(
      context.props.context,
      context.props.item,
      context.props.value
    );

    const externalLink = getExternalLink(
      context.props.context,
      context.props.item,
      context.props.value
    );

    const iconSuffix = iconSuffixExtractor(
      context.props.context,
      context.props.item,
      context.props.value
    );

    let iconSuffixRender;

    if (iconSuffix) {
      iconSuffixRender = createElement("v-icon", iconSuffix);
    }

    const cellContentList = [
      " ",
      cellContent(
        context.props.value, // Spaces required for tests to pass (old template used to have whitespace)
        context.props.context,
        context.props.item
      ),
      " ",
      iconSuffixRender,
    ];

    const emailExtractor =
      context.props.context.rendererMetadata.emailExtractor;
    const phoneExtractor =
      context.props.context.rendererMetadata.phoneExtractor;
    let prefix = "";
    let extractedItem = "";

    if (emailExtractor) {
      prefix = "mailto:";
      extractedItem = emailExtractor(context.props.item, context.props.value);
    } else if (phoneExtractor) {
      prefix = "tel:";
      extractedItem = phoneExtractor(context.props.item, context.props.value);
    }

    if (emailExtractor || phoneExtractor) {
      return extractedItem
        ? createElement(
            "a",
            {
              class: {
                "small-device-text": context.parent.$vuetify.breakpoint.xs,
              },
              domProps: {
                href: prefix + extractedItem,
              },
            },
            cellContentList
          )
        : createElement(
            "p",
            {
              class: {
                "small-device-text": context.parent.$vuetify.breakpoint.xs,
              },
            },
            cellContentList
          );
    }

    if (externalLink) {
      return createElement(
        "a",
        {
          class: {
            "small-device-text": context.parent.$vuetify.breakpoint.xs,
          },
          domProps: {
            target: context.props.context.rendererMetadata?.newTab
              ? "_blank"
              : "_self",
            href: externalLink,
          },
        },
        cellContentList
      );
    }

    return location
      ? createElement(
          "router-link",
          {
            class: {
              "bm-grid-clickable": !!context.listeners.edit,
              "small-device-text": context.parent.$vuetify.breakpoint.xs,
            },
            props: { to: location },
            domProps: {
              target: context.props.context.rendererMetadata?.newTab
                ? "_blank"
                : "_self",
              rel: "noopener noreferrer",
            },
          },
          cellContentList
        )
      : createElement(
          "p",
          {
            class: {
              "bm-grid-clickable": !!context.listeners.edit,
              "small-device-text": context.parent.$vuetify.breakpoint.xs,
            },
            on: context.listeners.edit
              ? {
                  click: (event: any) => {
                    event.stopPropagation();
                    // Prevents routing actions when we want to open an info-dialog
                    event.preventDefault();
                    context.listeners.edit &&
                      (context.listeners.edit as Function)(event);
                  },
                }
              : {},
          },
          cellContentList
        );
  },
});
</script>

<style scoped lang="sass">
span
  white-space: nowrap

.card-fields span.small-device-text
  white-space: normal
  overflow-wrap: break-word

a, p
  white-space: nowrap
  overflow: hidden
  text-overflow: ellipsis
</style>
